'use client'
import { PrismicRichText, PrismicImage } from "@prismicio/react";
import { isFilled } from "@prismicio/client";
import { motion } from "framer-motion";
import { useState, useEffect, useRef } from "react";

/**
 * @typedef {import("@prismicio/client").Content.AboutSlice} AboutSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<AboutSlice>} AboutProps
 * @type {import("react").FC<AboutProps>}
 */
const About = ({ slice, index, slices }) => {
  const isLast = Array.isArray(slices) && index === slices.length - 1;
  const fallbackAlt = "Image de contenu";
  const sectionRef = useRef(null);
  const [isBlinkVisible, setIsBlinkVisible] = useState(true);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setIsBlinkVisible((prev) => !prev);
    }, 700);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const blinkingSpans = section.querySelectorAll("span.clignotement");
    blinkingSpans.forEach((span) => {
      span.classList.toggle("opacity-0", !isBlinkVisible);
      span.classList.toggle("opacity-100", isBlinkVisible);
    });
  }, [isBlinkVisible, slice]);

  console.log(slice.primary.birth);
  
  return (
    <section
      ref={sectionRef}
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`bg-white ${isLast ? "md:pb-10" : ""} ${slice.variation === "now" ? "mt-10" : ""}`}
    >
      {slice.variation === "default" && (
        <div className="w-full max-w-350 w-auto mx-auto px-4 md:px-10">
          <div className="flex flex-col md:flex-row gap-10 xl:gap-20 relative">
            <div className="flex flex-col md:flex-row items-center md:items-end w-full md:w-1/2 xl:w-1/3 xl:pb-10 relative top-0">
              <div className="md:hidden w-[1px] h-full bg-light-gray absolute z-0">

              </div>
              {isFilled.richText(slice.primary.title) && (
                <PrismicRichText
                  field={slice.primary.title}
                  components={{
                    heading1: ({ children }) => (
                      <h1 className="font-neue-haas text-8xl font-medium uppercase pt-20 pb-10 md:py-0 relative z-10">{children}<br />{children}<br />{children}</h1>
                    ),
                  }}
                />
              )}
              <div className="md:hidden flex w-full flex-col items-center justify-center relative z-10">
                {isFilled.image(slice.primary.img) && (
                  <PrismicImage field={slice.primary.img} alt={slice.primary.img?.alt || fallbackAlt} className="aspect-square object-cover rounded-full w-full max-w-75 xl:max-w-100" />
                )}
              </div>
            </div>
            <div className="hidden md:flex w-[1px] xl:w-1/3  flex-col items-center justify-center">
              <div className="w-[1px] h-full bg-light-gray">
              </div>
              {isFilled.image(slice.primary.img) && (
                <PrismicImage field={slice.primary.img} alt={slice.primary.img?.alt || fallbackAlt} className="aspect-square object-cover rounded-full w-75 xl:w-full absolute xl:relative -bottom-75 xl:bottom-0 xl:max-w-100" />
              )}
            </div>
            <div className="h-full w-full md:w-1/2 xl:w-1/3 md:pt-36 pb-5">
              {isFilled.richText(slice.primary.text) && (
                <PrismicRichText
                  field={slice.primary.text}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="font-neue-haas text-lg/6 font-light text-end">{children}</p>
                    ),
                    hyperlink: ({ children, node }) => (
                      <a
                        href={node.data.url}
                        target={node.data.target}
                        className="underline hover:no-underline transition-all"
                      >
                        {children}
                      </a>
                    ),
                  }}
                />
              )}
            </div>
          </div>
          <div className="w-full mt-20 md:mt-80 xl:mt-20">
            {isFilled.richText(slice.primary.birth) && (
              <PrismicRichText
                field={slice.primary.birth}
                components={{
                  paragraph: ({ children }) => (
                    <h2 className="text-end font-neue-haas text-6xl font-medium uppercase mb-2">{children}</h2>
                  ),
                }}
              />
            )}
          </div>
        </div>
      )}

      {slice.variation === "now" && (
        <div className="">
          <div className="w-full md:flex items-center gap-4">
            <div className="h-[1px] w-full bg-light-gray flex-1 mb-10 md:mb-0"></div>
            {isFilled.richText(slice.primary.title) && (
              <PrismicRichText
                field={slice.primary.title}
                components={{
                  heading2: ({ children }) => (
                    <h2 className="font-neue-haas text-center md:text-start text-sm md:text-xl px-4 md:px-0 font-medium uppercase">{children}</h2>
                  ),
                }}
              />
            )}
            <div className="hidden md:block h-[1px] w-full bg-light-gray flex-1"></div>
          </div>
          <div className="w-full max-w-350 w-auto mx-auto md:px-10">
            <div className="flex flex-col md:flex-row pt-2 pb-10">
              <div className="w-full md:w-1/2 md:pr-10 flex flex-col justify-between px-4 md:px-0 py-10 md:py-0">
                <div className="w-fit mb-20">
                  {isFilled.richText(slice.primary.text_decoration) && (
                    <PrismicRichText
                      field={slice.primary.text_decoration}
                      components={{
                        paragraph: ({ children }) => (
                          <p className="font-neue-haas text-lg font-extralight text-end">{children}</p>
                        ),
                      }}
                    />
                  )}
                </div>
                <div className="w-full mb-40">
                  {isFilled.richText(slice.primary.text) && (
                    <PrismicRichText
                      field={slice.primary.text}
                      components={{
                        paragraph: ({ children }) => (
                          <p className="font-neue-haas text-lg font-light text-end">{children}</p>
                        ),
                        strong: ({ children }) => (
                          <strong className="font-medium">{children}</strong>
                        ),
                        em: ({ children }) => (
                          <em className="italic">{children}</em>
                        ),
                      }}
                    />
                  )}
                </div>
                <div className="w-full mb-20 pl-5">
                  {isFilled.richText(slice.primary.text_cv) && (
                    <PrismicRichText
                      field={slice.primary.text_cv}
                      components={{
                        paragraph: ({ children }) => (
                          <p className="font-neue-haas text-lg font-light">{children}</p>
                        ),
                        em: ({ children }) => (
                          <em className="italic">{children}</em>
                        ),
                        strong: ({ children }) => (
                          <strong className="font-medium">{children}</strong>
                        ),
                      }}
                    />
                  )}
                </div>
                <div className="pl-5">
                  <h3 className="font-neue-haas text-5xl font-normal">{slice.primary.activity}</h3>
                </div>
              </div>
              <div className="w-full h-[1px] md:h-auto md:w-[1px] bg-light-gray mb-15 lg:mb-34"></div>
              <div className="w-full md:w-1/2 flex flex-col justify-end items-end px-4 md:px-0 pt-10 md:py-0">
                {isFilled.image(slice.primary.img) && (
                  <PrismicImage field={slice.primary.img} alt={slice.primary.img?.alt || fallbackAlt} className="h-110 w-auto mb-10" />
                )}
                <div className="w-full flex gap-4 justify-between items-end">
                  <img src="/mw-arrow.svg" alt="flèche" className="w-16 lg:w-40" />
                  <div className="flex flex-col">
                    {isFilled.richText(slice.primary.text_contact) && (
                      <PrismicRichText
                        field={slice.primary.text_contact}
                        components={{
                          paragraph: ({ children }) => (
                            <p className="font-neue-haas text-base lg:text-xl xl:text-2xl font-normal uppercase text-end border-t-1 border-light-gray last:border-b-1">{children}</p>
                          ),
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default About;